import { supabase } from '../supabase';

/**
 * Ordini di magazzino collegati a un'offerta che si sta eliminando.
 *
 * Regola: gli ordini confermati o successivi (e gli annullati) sono documenti
 * di magazzino e restano, scollegati dall'offerta con una nota interna. Per gli
 * ordini in bozza si chiede se eliminarli insieme all'offerta; chiudendo la
 * domanda restano, scollegati (scelta prudente). L'esecuzione è in
 * `dealStore.deleteDeal`; qui solo lettura e domanda, uguali in tutte le
 * schermate da cui si elimina un'offerta.
 */

export interface DealLinkedOrderInfo {
  id: string;
  order_number: string;
  status: string;
}

type ConfirmFn = (options: {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'danger' | 'primary';
}) => Promise<boolean>;

export interface DealLinkedOrdersChoice {
  /** Eliminare gli ordini in bozza insieme all'offerta */
  deleteDraftOrders: boolean;
  /** Frase da aggiungere alla conferma finale (vuota se non ci sono ordini) */
  summary: string;
}

export async function loadDealLinkedOrders(dealId: string): Promise<DealLinkedOrderInfo[]> {
  const { data, error } = await supabase
    .from('orders')
    .select('id, order_number, status')
    .eq('deal_id', dealId)
    .order('created_at', { ascending: true });
  if (error) {
    console.warn('Ordini collegati all\'offerta non letti:', error);
    return [];
  }
  return (data ?? []) as DealLinkedOrderInfo[];
}

const list = (orders: DealLinkedOrderInfo[]) => orders.map((o) => o.order_number).join(', ');

export async function askDealLinkedOrders(dealId: string, confirm: ConfirmFn): Promise<DealLinkedOrdersChoice> {
  const orders = await loadDealLinkedOrders(dealId);
  if (orders.length === 0) return { deleteDraftOrders: false, summary: '' };

  const drafts = orders.filter((o) => o.status === 'draft');
  const others = orders.filter((o) => o.status !== 'draft');

  const othersText = others.length === 0
    ? ''
    : others.length === 1
      ? `L'ordine ${list(others)} resta in magazzino, scollegato dall'offerta.`
      : `Gli ordini ${list(others)} restano in magazzino, scollegati dall'offerta.`;

  let deleteDraftOrders = false;
  if (drafts.length > 0) {
    deleteDraftOrders = await confirm({
      title: drafts.length === 1 ? 'Ordine in bozza collegato' : 'Ordini in bozza collegati',
      message: (drafts.length === 1
        ? `Questa offerta ha l'ordine in bozza ${list(drafts)}. Vuoi eliminarlo insieme all'offerta o tenerlo scollegato?`
        : `Questa offerta ha gli ordini in bozza ${list(drafts)}. Vuoi eliminarli insieme all'offerta o tenerli scollegati?`)
        + (othersText ? ` ${othersText}` : ''),
      confirmText: drafts.length === 1 ? "Elimina anche l'ordine" : 'Elimina anche gli ordini',
      cancelText: drafts.length === 1 ? 'Tienilo scollegato' : 'Tienili scollegati',
      confirmVariant: 'danger',
    });
  }

  const draftsText = drafts.length === 0
    ? ''
    : deleteDraftOrders
      ? (drafts.length === 1 ? `L'ordine in bozza ${list(drafts)} verrà eliminato.` : `Gli ordini in bozza ${list(drafts)} verranno eliminati.`)
      : (drafts.length === 1 ? `L'ordine in bozza ${list(drafts)} resta, scollegato dall'offerta.` : `Gli ordini in bozza ${list(drafts)} restano, scollegati dall'offerta.`);

  return { deleteDraftOrders, summary: [draftsText, othersText].filter(Boolean).join(' ') };
}
